/**
 * LodZoomPolicy — maps the explore camera's fractional zoom level to a target base LOD.
 *
 * Pure and stateful-by-module (like ExploreCamera): the raw log2 zoom is EMA-smoothed so wheel
 * bursts don't thrash, and promotion/demotion use a hysteresis band around each integer boundary
 * so hovering at a boundary never flaps the base LOD back and forth. The result is a TARGET —
 * VoxelWorld steps its displayed base LOD toward it one level at a time as coverage permits.
 */

/** Smoothing time constant (ms) for the raw zoom level. */
const EMA_TAU_MS = 150;

/** Hysteresis half-band around integer boundaries: promote at L+1+BAND, demote at L+1−BAND. */
const BAND = 0.25;

let smoothed = 0;
let targetBaseLod = 0;

/** Reset (entering first person / world switch): target snaps to 0. */
export function resetLodZoom(): void {
  smoothed = 0;
  targetBaseLod = 0;
}

/**
 * Advance the policy with the current fractional zoom level (ExploreCamera.getExploreZoomLevel).
 * @returns the hysteretic target base LOD, clamped to [0, maxBaseLod].
 */
export function updateLodZoom(deltaMs: number, zoomLevel: number, maxBaseLod: number): number {
  const alpha = 1 - Math.exp(-deltaMs / EMA_TAU_MS);
  smoothed += (zoomLevel - smoothed) * alpha;

  // Promote while the smoothed level sits clearly above the next boundary; demote when clearly
  // below the current one. In between: hold (the hysteresis band).
  while (targetBaseLod < maxBaseLod && smoothed >= targetBaseLod + 1 + BAND) targetBaseLod++;
  while (targetBaseLod > 0 && smoothed <= targetBaseLod - BAND) targetBaseLod--;
  if (targetBaseLod > maxBaseLod) targetBaseLod = maxBaseLod;
  return targetBaseLod;
}

/** Current smoothed zoom level (debug display). */
export function getSmoothedZoomLevel(): number {
  return smoothed;
}
